import React from 'react'
import { Table } from 'antd'
import { IStep } from 'egi/types'
import { ProposalState } from 'store/modules/proposal/types'

type Props = {
  cndCallbackPayload: any
  proposal: ProposalState
  step: IStep
}

type TableRow = {
  key: string
  description: string
  result: React.ReactNode
}

function toText (value: unknown, fallback = '-') {
  if (value === undefined || value === null || value === '') return fallback
  return String(value)
}

const SEARCH_STATUS_LABELS: Record<string, string> = {
  PROCESSING: 'pendente',
  PROCESSED: 'concluído',
  ERROR: 'falha'
}

const ENVOLVED_KIND_LABELS: Record<string, string> = {
  POLE_PASSIVE: 'passivo',
  POLE_ACTIVE: 'ativo'
}

function formatSearchStatus (status: unknown) {
  if (status === undefined || status === null || status === '') return '-'
  return SEARCH_STATUS_LABELS[String(status)] ?? '-'
}

function formatEnvolvedKind (envolvedKind: unknown) {
  if (envolvedKind === undefined || envolvedKind === null || envolvedKind === '') return '-'
  return ENVOLVED_KIND_LABELS[String(envolvedKind)] ?? String(envolvedKind)
}

function isConjugeStep (step: IStep) {
  return Boolean(step.ref?.includes('conjuge'))
}

function getDepartmentData (isConjuge:boolean, proposal:ProposalState) {
  if (isConjuge) {
    return {
      name: proposal.departments?.[1],
      cpf: proposal.departments?.[1]
    }
  }
  return {
    name: proposal.departments?.[0],
    cpf: proposal.departments?.[0]
  }
}

function getPersonName (isConjuge:boolean, proposal:ProposalState) {
  const departmentData = getDepartmentData(isConjuge, proposal)
  return departmentData.name?.steps?.[0]?.form?.name
}

function getPersonCpf (isConjuge:boolean, proposal:ProposalState) {
  const departmentData = getDepartmentData(isConjuge, proposal)
  return departmentData.cpf?.steps?.[0]?.form?.cpf
}

export default function CndCivilDistributionJsonView ({ cndCallbackPayload, proposal, step }: Props) {
  if (!cndCallbackPayload) return null

  let payloadParsed = cndCallbackPayload
  if (typeof cndCallbackPayload === 'string') {
    try {
      payloadParsed = JSON.parse(cndCallbackPayload)
    } catch {
      payloadParsed = {}
    }
  }

  const payload = payloadParsed
  const serviceInfo = payload?.service_infos?.[0]?.data
  const processesRaw = serviceInfo?.child_process
  const processesList = Array.isArray(processesRaw) ? processesRaw : []
  const hasProcesses = processesList.length > 0

  const isConjuge = isConjugeStep(step)
  const name = getPersonName(isConjuge, proposal)
  const cpf = getPersonCpf(isConjuge, proposal)

  const createdAtRaw = payload?.created_at
  const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null
  const createdAtFormatted = createdAtDate !== null && !Number.isNaN(createdAtDate.getTime())
    ? {
      date: createdAtDate.toLocaleDateString('pt-BR'),
      hour: createdAtDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }
    : null

  function getUniqueValues (values: unknown[]) {
    return Array.from(
      new Set(
        values
          .filter(value => value !== undefined && value !== null && value !== '')
          .map(value => String(value))
      )
    )
  }

  const authCodes = getUniqueValues([
    payload?.auth_code,
    serviceInfo?.auth_code,
    ...processesList.map(process => process?.auth_code),
    ...processesList.map(process => process?.process_link)
  ])

  const processNumbers = getUniqueValues([
    ...processesList.map(process => process?.process_number)
  ])

  const childProcessNotFoundText = 'Não encontrado'
  const statusByProcess = hasProcesses ? 'Positiva' : 'Negativa'
  const isAuthCodeNotFound = authCodes.length === 0
  const isProcessNumberNotFound = processNumbers.length === 0
  const searchStatus = isAuthCodeNotFound && isProcessNumberNotFound
    ? 'Orgão indisponível no momento'
    : formatSearchStatus(payload?.status)

  function renderArrayValue (values: string[], emptyText: string, isLink = false) {
    if (values.length === 0) return emptyText
    if (values.length === 1) {
      const singleValue = values[0]
      if (isLink && singleValue.startsWith('http')) {
        return (
          <a href={singleValue} target='_blank' rel='noreferrer'>
            {singleValue}
          </a>
        )
      }
      return singleValue
    }

    return (
      <ol className='mb-0 pl-3'>
        {values.map((value, index) => (
          <li key={`${value}-${index}`}>
            {isLink && value.startsWith('http')
              ? (
                <a href={value} target='_blank' rel='noreferrer'>
                  {value}
                </a>
              )
              : value}
          </li>
        ))}
      </ol>
    )
  }

  const authCodeResult = renderArrayValue(authCodes, childProcessNotFoundText, true)
  const processNumberResult = renderArrayValue(processNumbers, childProcessNotFoundText)

  const rows: TableRow[] = [
    { key: 'name', description: 'Nome', result: toText(name) },
    { key: 'cpf', description: 'CPF', result: toText(cpf) },
    { key: 'total_processes', description: 'Quantidade de processos', result: toText(serviceInfo?.total_processes, '0') },
    { key: 'status', description: 'Status', result: statusByProcess },
    { key: 'auth_code', description: 'Código de autenticação da certidão', result: authCodeResult },
    { key: 'process_number', description: 'Número dos processos', result: processNumberResult },
    { key: 'envolved_kind', description: 'Tipo de participação', result: formatEnvolvedKind(serviceInfo?.envolved_kind) },
    { key: 'search_status', description: 'Status da busca', result: searchStatus },
    { key: 'date', description: 'Data', result: createdAtFormatted?.date || '-' },
    { key: 'hour', description: 'Horário', result: createdAtFormatted?.hour || '-' }
  ]

  return (
    <section className='mb-3 mt-3'>
      <h3 className='mb-3'>Propriedades da API</h3>
      <Table
        className='mb-2'
        dataSource={rows}
        pagination={false}
        size='small'
        rowKey='key'
        columns={[
          {
            title: 'Descrição',
            dataIndex: 'description',
            key: 'description'
          },
          {
            title: 'Resultado',
            dataIndex: 'result',
            key: 'result',
            render: (value: React.ReactNode) => value
          }
        ]}
      />
    </section>
  )
}
